import React from 'react';

export interface ExpenseChartData {
  category: string;
  amount: number;
  color?: string;
}

export interface ExpenseChartProps {
  data: ExpenseChartData[];
  isLoading?: boolean;
  height?: number;
}

const ExpenseChart: React.FC<ExpenseChartProps> = ({ data, isLoading, height = 300 }) => {
  const colors = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#3b82f6'];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Expenses by Category</h2>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Expenses by Category</h2>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No expense data available
        </div>
      </div>
    );
  }

  const maxAmount = Math.max(...data.map(d => d.amount), 0);
  const chartHeight = height - 100;
  const barWidth = Math.max(30, (800 / data.length) - 10);
  const spacing = 10;

  const total = data.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Expenses by Category</h2>
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-2" style={{ height: `${chartHeight}px` }}>
          {data.map((item, index) => {
            const heightPercent = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
            const barHeight = (chartHeight * heightPercent) / 100;
            const color = item.color || colors[index % colors.length];
            
            return (
              <div
                key={index}
                className="flex-1 flex flex-col items-center group relative"
                style={{ minWidth: `${barWidth}px` }}
              >
                <div className="w-full flex flex-col items-center justify-end" style={{ height: `${chartHeight}px` }}>
                  <div
                    className="w-full rounded-t transition-all hover:opacity-80 cursor-pointer relative"
                    style={{
                      height: `${barHeight}px`,
                      backgroundColor: color,
                      minHeight: barHeight > 0 ? '4px' : '0',
                    }}
                  >
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      €{item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-600 text-center truncate w-full" title={item.category}>
                  {item.category}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {((item.amount / total) * 100).toFixed(1)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExpenseChart;

